import _ from "lodash";
import PropTypes from "prop-types";
import React from "react";
import { Router, browserHistory, applyRouterMiddleware } from "react-router";
import { Provider, connect } from "react-redux";
import { DragDropEvent, colors } from "Constants";
import { DragDropContext } from "react-beautiful-dnd";
import {
  shouldRerouteAfterError,
  cloudFrontDomain,
  checkCookiesEnabled,
  recordDesignSystemUsageMetrics,
  getIsChina,
} from "config";
import { Sentry } from "services/sentry";
import { setJsonItem } from "services/localStorage";
import UnderMaintanance from "AppComponents/UnderMaintanance";
import { i18n } from "lib/i18n";
import { DSProvider } from "@toddle-edu/ds-web";
import DialogueBoxV2 from "UIComponents/DialogueBoxV2";
import { persistor } from "lib/apolloClient";
import { restoreScroll } from "@toddle-edu/react-router-scroll";
import { scrollUpdateCallback, excludedRoutes } from "Utils";
import classes from "./AppContainer.scss";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import dsComponentMetricsManager from "Utils/DSMetricsUtils/RegisterDSComponentManager";
import { BackgroundQueryProvider } from "lib/apolloClient/backgroundQueryProvider";

const styles = {
  dialogueBox: {
    headerStyles: {
      padding: "24px 24px 16px 32px",
      alignItems: "center",
    },
    modalBodyStyle: {
      padding: "0px 32px 8px",
      fontSize: 16,
      fontWeight: 500,
      color: colors.textDefault,
    },
    footerContainerStyle: {
      padding: "24px 16px",
    },
    headerTitleStyles: {
      fontSize: 20,
      fontWeight: 700,
      color: colors.textCritical,
    },
  },
};

class AppContainer extends React.Component {
  static propTypes = {
    history: PropTypes.object.isRequired,
    routes: PropTypes.object.isRequired,
    routerKey: PropTypes.number,
    store: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      language: "en",
      showErrorModal: false,
    };
    this.clearExternalStorage = false;
    this.timerForHardReloading = null;
    this.unlisten = null;

    // Create stable reference to registerComponent function
    this.registerComponent = this.registerComponent.bind(this);

    //Ref for maintaining background queries' metadata
    this.prefetchRegistryRef = React.createRef();
    this.prefetchRegistryRef.current = {};
  }

  /**
   * Stable method for registering DS component usage
   * Bound in constructor to maintain consistent reference
   * Only registers when recordDesignSystemUsageMetrics is enabled
   */
  registerComponent(metricData) {
    if (recordDesignSystemUsageMetrics) {
      dsComponentMetricsManager.registerComponent(metricData);
    }
  }

  UNSAFE_componentWillMount() {
    this.debounceRerouteAfterError = this.debounce(
      this.rerouteAfterError,
      1000
    );
  }

  componentDidMount() {
    window.addEventListener("click", this.handleClick, { capture: true });
    i18n.on("languageChanged", lng => {
      this.updateLanguage(lng);
    });
    window.addEventListener("keydown", this.handleKeyDown);
    window.addEventListener("beforeunload", this.handleOnBeforeReload);
    window.addEventListener("error", this.manageLoadOrRunTimeErrors);
    window.addEventListener(
      "unhandledrejection",
      this.manageUnHandledExceptions
    );
    if (recordDesignSystemUsageMetrics) {
      window.addEventListener("visibilitychange", this.handleVisibilityChange);

      // Listen to location changes
      this.unlisten = this.props.history.listen(() => {
        dsComponentMetricsManager.flushRegistry();
      });
    }

    const checkCookiesAllowed = domain => {
      try {
        if (!checkCookiesEnabled) {
          return true;
        }

        // Attempt to set a test cookie
        // Domain=${setCookieDomain}; Secure; HttpOnly; SameSite=None; Path=/;
        document.cookie = `test_cookie=1; Domain=${domain}; Secure; SameSite=None; Path=/;`;
        const cookiesEnabled = document.cookie.includes("test_cookie=1");

        // Cleanup the test cookie
        document.cookie = `test_cookie=1; Domain=${domain}; expires=Thu, 01 Jan 1970 00:00:00 UTC; Secure; SameSite=None; Path=/;`;

        return cookiesEnabled;
      } catch (e) {
        return false;
      }
    };

    if (!checkCookiesAllowed(cloudFrontDomain)) {
      this.setState({ showErrorModal: true });
    }
  }

  componentWillUnmount() {
    window.removeEventListener("click", this.handleClick, { capture: true });
    window.removeEventListener("keydown", this.handleKeyDown);
    window.removeEventListener("beforeunload", this.handleOnBeforeReload);
    window.removeEventListener("error", this.manageLoadOrRunTimeErrors);
    window.removeEventListener(
      "unhandledrejection",
      this.manageUnHandledExceptions
    );
    if (recordDesignSystemUsageMetrics) {
      window.removeEventListener(
        "visibilitychange",
        this.handleVisibilityChange
      );
    }

    // Cleanup history listener
    if (this.unlisten) {
      this.unlisten();
    }
  }

  handleTryAgain = () => {
    this.setState({ showErrorModal: false });
    window.location.reload();
  };

  handleClose = () => {
    this.setState({ showErrorModal: false });
  };

  handleVisibilityChange = () => {
    if (document.visibilityState === "hidden") {
      dsComponentMetricsManager.flushRegistry();
    }
  };

  manageLoadOrRunTimeErrors = error => {
    const errorMessagesForMutatingFrozenData = [
      "Cannot assign to read only property",
      "Cannot add property",
      "Cannot delete property",
    ];

    if (
      _.some(errorMessagesForMutatingFrozenData, message =>
        _.includes(error.message, message)
      )
    ) {
      Sentry.setTag("reason", "MUTATING_FROZEN_DATA");
      Sentry.captureMessage(`${error.message}`, {
        extra: {
          reason: "MUTATING_FROZEN_DATA",
        },
      });
    }
  };

  //Reference: https://developer.mozilla.org/en-US/docs/Web/API/Window/unhandledrejection_event
  manageUnHandledExceptions = event => {
    const indexedDbRelatedErrors = ["UnknownError", "indexedDB.open"];

    const errorMessage = _.get(event, "reason");
    const rejectedPromise = _.get(event, "promise");

    if (
      _.some(indexedDbRelatedErrors, error => _.includes(errorMessage, error))
    ) {
      rejectedPromise?.catch?.(() => {
        persistor.pause();
        setJsonItem({ key: "stopRestoringDataFromIndexedDb", value: true });
      });

      Sentry.captureMessage(`INDEXED_DB_ERROR:${errorMessage}`, {
        level: "info",
        tags: {
          origin: "UNHANDLED_REJECTION_APP_CONTAINER",
        },
        extra: event,
      });

      event.preventDefault(); //To stop logging error in console
    }
  };

  handleKeyDown = e => {
    const shortcutValuesRelatedToHardReload = [
      e.keyCode == 82 && e.metaKey && e.shiftKey, //Cmd + Shift + r -> Chrome/Firefox on Mac
      e.metaKey && e.keyCode === 116, // Ctrl + F5 -> Chrome/Firefox/Edge on Windows and Linux
      e.metaKey && e.altKey && e.keyCode == 69, // Cmd + Option + e -> For safari;
    ];

    if (_.some(shortcutValuesRelatedToHardReload, value => value)) {
      this.clearExternalStorage = true;

      /**
       * We need timer,because user can accidently use shortcut keys which do not hard refresh site in particular browser.
       * In this case, we need a way to revert change in clearExternalStorage, otherwise storage will be cleared in simple reload.
       * So, we use timeout to solve this issue.
       */
      this.timerForHardReloading = setTimeout(() => {
        this.clearExternalStorage = false;
        this.timerForHardReloading = null;
      }, 2000);
    }
  };

  handleOnBeforeReload = async event => {
    if (this.clearExternalStorage) {
      this.clearExternalStorage = false;
      clearTimeout(this.timerForHardReloading);
      await persistor.purge();
    }
    if (this.props.isUploadInProgress) {
      const confirmationMessage =
        "You have an ongoing file upload. Are you sure you want to leave this page?";
      event.returnValue = confirmationMessage;
      return confirmationMessage;
    }
  };

  handleClick = e => {
    window.shouldOpenInNewTab = false;
    if (e.metaKey || e.ctrlKey) {
      window.shouldOpenInNewTab = true;
    }
  };

  componentDidCatch = (error, info) => {
    if (shouldRerouteAfterError) {
      Sentry.withScope(scope => {
        scope.setTag("origin", "componentDidCatch");
        // eslint-disable-next-line @toddle-edu/common/no-sentry-capture-exception
        Sentry.captureException(error);
      });

      this.debounceRerouteAfterError();
    } else {
      //Logs error in console when env not prod
      console.error(error, info);
    }
  };

  rerouteAfterError = async () => {
    // if (this.prevLocation != window.location.pathname) {
    //   console.log("Hello");
    //   window.location.reload();
    //   browserHistory.goBack();
    //   localStorage.setItem("errorReload", true);
    // } else {
    //   console.log("Lalal");
    //   const pathArray = window.location.pathname.split("/");
    //   if (pathArray.length > 2) {
    //     browserHistory.push(`/${pathArray[1]}`);
    //   } else {
    //     browserHistory.push(`/`);
    //   }
    //   localStorage.setItem("errorReload", true);
    //   window.location.reload();
    // }

    const pathArray = window.location.pathname.split("/");
    if (pathArray.length > 2) {
      browserHistory.push(`/${pathArray[1]}`);
    } else {
      browserHistory.push(`/`);
    }
    if (localStorage) {
      localStorage.setItem("errorReload", true);
    }

    /**
     * When page is reloaded due to error, we need to clear indexedDb data, so that cache
     * is not populated with possibly corrupted data, after reloading page.
     */
    await persistor.purge();

    window.location.reload();

    // console.log(window.location);
    // if (pathArray.length > 2) {
    //   let reRoutePath = `/${pathArray[1]}/${pathArray[2]}`;
    //   browserHistory.push(reRoutePath);
    // } else {
    //   switch (pathArray[1]) {
    //     case "teacher":
    //       browserHistory.push("/teacher/classes");
    //       break;
    //     case "admin":
    //       browserHistory.push("/admin");
    //       break;
    //     case "administrator":
    //       browserHistory.push("/administrator");
    //       break;
    //     default:
    //       browserHistory.push("/");
    //   }
    // }
  };

  debounce = (func, wait, immediate) => {
    var timeout;
    return function () {
      var context = this,
        args = arguments;
      var later = function () {
        timeout = null;
        if (!immediate) func.apply(context, args);
      };
      var callNow = immediate && !timeout;
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
      if (callNow) func.apply(context, args);
    };
  };

  onDragEnd = params => {
    //Emit onDrag event
    document.body.style.pointerEvents = "unset";
    DragDropEvent.emit("onDragEnd", params);
  };

  onDragStart = params => {
    document.body.style.pointerEvents = "none";
    DragDropEvent.emit("onDragStart", params);
  };

  updateLanguage(lang) {
    this.setState({ ...this.state, language: lang });
  }

  render() {
    const { history, routes, store, underMaintenance } = this.props;
    const { showErrorModal } = this.state;

    const element = !_.isEmpty(underMaintenance) ? (
      <UnderMaintanance underMaintenance={underMaintenance} />
    ) : (
      <Provider store={store}>
        <DSProvider
          language={this.state.language}
          isChina={getIsChina()}
          orgId={getUserInfo?.()?.org_id}
          registerComponent={this.registerComponent}
        >
          <div style={{ height: "100%" }}>
            <DragDropContext
              onDragEnd={this.onDragEnd}
              onDragStart={this.onDragStart}
            >
              <Router
                history={history}
                render={applyRouterMiddleware(
                  restoreScroll(scrollUpdateCallback, excludedRoutes)
                )}
              >
                {routes}
              </Router>
            </DragDropContext>
            {showErrorModal && (
              <DialogueBoxV2
                modalTitle={i18n.t("common:cookies_disabled_title")}
                modalBody={i18n.t("common:cookies_disabled_message")}
                button1={"Try Again"}
                button1Props={{ variant: "outlined", size: "large" }}
                onClickButton1={this.handleTryAgain}
                toggleDialogueBoxDisplay={this.handleClose}
                customModalContentClass={classes.customModalStyle}
                {...styles.dialogueBox}
                showCloseButton={true}
              />
            )}
          </div>
        </DSProvider>
      </Provider>
    );

    return (
      <BackgroundQueryProvider prefetchRegistryRef={this.prefetchRegistryRef}>
        {element}
      </BackgroundQueryProvider>
    );
  }
}

const mapStateToProps = state => {
  const underMaintenance = _.get(state, "login.underMaintenance", false);
  const isUploadInProgress = !_.isEmpty(
    _.get(state, "app_services.progressOfUploads", {})
  );
  return { underMaintenance, isUploadInProgress };
};

export default connect(mapStateToProps)(AppContainer);
